import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { invoiceService } from '../services/api';
import { Invoice, User } from '../types';
import { defaultPaymentYear, getPaymentYearOptions } from '../utils/paymentYears';
import {
  computeUnpaidRentMonthsBeforeCurrent,
  CurrentMonthRentStatus,
  getCurrentMonthRentStatus,
  rentPeriodKey,
} from '../utils/tenantPaymentStatus';
import '../pages/TenantManagement.css';

const PAYMENT_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export type PayInvoiceForm = {
  paymentMonth: string;
  paymentYear: string;
  paymentType: Invoice['paymentType'];
  paidAmount: string;
  balanceAmount: string;
  discount: string;
  dueDate: string;
};

type UnpaidRentRow = {
  month: string;
  year: string;
  periodLabel: string;
  rent: number;
  status: 'Due';
  balanceDue: number;
  existingInvoiceId?: number;
};

function toYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultDueDateYmd(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return toYMD(d);
}

function emptyPayForm(tenant: User): PayInvoiceForm {
  const now = new Date();
  const rent = tenant.rent != null && tenant.rent !== '' ? String(tenant.rent) : '';
  return {
    paymentMonth: now.toLocaleString('default', { month: 'long' }),
    paymentYear: defaultPaymentYear(),
    paymentType: 'RENT',
    paidAmount: rent,
    balanceAmount: '',
    discount: '',
    dueDate: '',
  };
}

function computeUnpaidRentMonths(tenant: User, invoices: Invoice[]): UnpaidRentRow[] {
  return computeUnpaidRentMonthsBeforeCurrent(tenant, invoices).map((row) => ({
    ...row,
    status: 'Due' as const,
  }));
}

function formatAdvance(t: User): string {
  const a = t.advance;
  if (a === undefined || a === null || a === '') return '—';
  const n = Number(a);
  return Number.isFinite(n) ? n.toLocaleString('en-IN') : String(a);
}

export interface PayInvoiceModalProps {
  tenant: User | null;
  initialForm?: Partial<PayInvoiceForm> | null;
  /** When paying from the Dues table, pass the due row id so it is cleared after payment. */
  dueId?: number | null;
  /** Invoice row to update (from due.invoiceId). */
  sourceInvoiceId?: number | null;
  /** Shown when settling a row from the Dues page (hides generic “still due” banners). */
  settleDueSummary?: { month: string; year: string; amount: number } | null;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
}

const PayInvoiceModal: React.FC<PayInvoiceModalProps> = ({
  tenant,
  initialForm,
  dueId,
  sourceInvoiceId,
  settleDueSummary,
  onClose,
  onSuccess,
}) => {
  const settleDueMode = dueId != null;
  const { user: authUser } = useAuth();
  const [payForm, setPayForm] = useState<PayInvoiceForm>(() => emptyPayForm({} as User));
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [payArrears, setPayArrears] = useState<UnpaidRentRow[]>([]);
  const [payCurrentMonth, setPayCurrentMonth] = useState<CurrentMonthRentStatus | null>(null);
  const [payArrearsLoading, setPayArrearsLoading] = useState(false);
  const [payArrearsSelectedKey, setPayArrearsSelectedKey] = useState<string | null>(null);

  const payYearOptions = getPaymentYearOptions();

  const loadPayContext = async (t: User) => {
    if (settleDueMode) {
      setPayArrears([]);
      setPayCurrentMonth(null);
      setPayArrearsLoading(false);
      return;
    }
    setPayArrearsLoading(true);
    try {
      const inv = await invoiceService.getUserInvoices(t.id);
      let arrears = computeUnpaidRentMonths(t, inv);
      const excludeKey =
        initialForm?.paymentMonth && initialForm?.paymentYear
          ? rentPeriodKey(initialForm.paymentMonth, initialForm.paymentYear)
          : null;
      if (excludeKey) {
        arrears = arrears.filter((row) => rentPeriodKey(row.month, row.year) !== excludeKey);
      }
      setPayArrears(arrears);
      setPayCurrentMonth(getCurrentMonthRentStatus(t, inv));
    } catch {
      setPayArrears([]);
      setPayCurrentMonth(null);
      toast.error('Could not load rent history for this user.');
    } finally {
      setPayArrearsLoading(false);
    }
  };

  useEffect(() => {
    if (!tenant) return;
    const base = emptyPayForm(tenant);
    const merged = initialForm ? { ...base, ...initialForm } : base;
    setPayForm(merged);
    setPayArrears([]);
    setPayCurrentMonth(null);
    setPayArrearsSelectedKey(
      initialForm?.paymentMonth && initialForm?.paymentYear
        ? `${initialForm.paymentMonth}|${initialForm.paymentYear}`
        : null
    );
    void loadPayContext(tenant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.id, initialForm]);

  if (!tenant) return null;

  const closeModal = () => {
    if (paySubmitting) return;
    onClose();
  };

  const applyArrearsRowToPayForm = (row: UnpaidRentRow) => {
    setPayArrearsSelectedKey(`${row.month}|${row.year}`);
    setPayForm({
      paymentMonth: row.month,
      paymentYear: row.year,
      paymentType: 'RENT',
      paidAmount: String(Math.round(row.balanceDue)),
      balanceAmount: '',
      discount: '',
      dueDate: '',
    });
  };

  const dueTotal =
    settleDueMode && settleDueSummary ? Math.round(settleDueSummary.amount) : 0;
  const payingNum =
    payForm.paidAmount.trim() === '' ? NaN : Number(payForm.paidAmount);
  const balanceAfterSettle =
    settleDueMode && Number.isFinite(payingNum)
      ? Math.max(0, dueTotal - payingNum)
      : dueTotal;

  const handlePayingAmountChange = (raw: string) => {
    if (settleDueMode && settleDueSummary) {
      const n = raw.trim() === '' ? NaN : Number(raw);
      if (Number.isFinite(n) && n > dueTotal + 0.01) {
        toast.error(
          `Paying amount cannot exceed due (₹${dueTotal.toLocaleString('en-IN')})`
        );
        return;
      }
      const remain = Number.isFinite(n) ? Math.max(0, Math.round(dueTotal - n)) : dueTotal;
      setPayForm((f) => ({
        ...f,
        paidAmount: raw,
        balanceAmount: String(remain),
      }));
      return;
    }
    setPayForm((f) => ({ ...f, paidAmount: raw }));
  };

  const handlePayBalanceChange = (raw: string) => {
    if (settleDueMode) return;
    const trimmed = raw.trim();
    const num = trimmed === '' ? NaN : Number(trimmed);
    const hasBalance = trimmed !== '' && Number.isFinite(num) && num > 0;
    setPayForm((f) => ({
      ...f,
      balanceAmount: raw,
      dueDate: hasBalance ? (f.dueDate?.trim() ? f.dueDate : defaultDueDateYmd()) : '',
    }));
  };

  const handleCreatePayInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenant?.id || !authUser?.id) {
      toast.error('Could not identify tenant or admin user.');
      return;
    }
    if (settleDueMode && !sourceInvoiceId) {
      toast.error('This due is not linked to an invoice. Create or link an invoice first.');
      return;
    }
    const payingRaw = payForm.paidAmount.trim();
    const payingAmount = payingRaw === '' ? NaN : Number(payingRaw);
    if (payingRaw === '' || !Number.isFinite(payingAmount) || payingAmount <= 0) {
      toast.error(settleDueMode ? 'Enter a valid paying amount.' : 'Enter a valid paid amount.');
      return;
    }
    if (settleDueMode && payingAmount > dueTotal + 0.01) {
      toast.error(`Paying amount cannot exceed due (₹${dueTotal.toLocaleString('en-IN')})`);
      return;
    }

    let balanceRaw = payForm.balanceAmount.trim();
    let balanceNum = balanceRaw === '' ? 0 : Number(balanceRaw);
    if (!settleDueMode) {
      if (balanceRaw !== '' && (!Number.isFinite(balanceNum) || balanceNum < 0)) {
        toast.error('Enter a valid balance amount.');
        return;
      }
      if (balanceNum > 0 && !payForm.dueDate.trim()) {
        toast.error('Due date is required when there is a balance.');
        return;
      }
    } else {
      balanceNum = Math.max(0, dueTotal - payingAmount);
    }

    setPaySubmitting(true);
    try {
      const discountRaw = payForm.discount.trim();
      let paymentStatus: Invoice['paymentStatus'] = 'PAID';
      if (balanceNum > 0) {
        paymentStatus = payingAmount > 0 ? 'PARTIAL' : 'PENDING';
      }

      if (settleDueMode && sourceInvoiceId != null && settleDueSummary) {
        const { invoice: updatedInv, receiptInvoice } = await invoiceService.applyInvoicePayment(
          sourceInvoiceId,
          {
            dueAmount: dueTotal,
            payingAmount,
            dueId: dueId ?? undefined,
            paymentTimestamp: new Date().toISOString(),
          }
        );
        if (receiptInvoice) {
          toast.info(
            `Payment receipt invoice #${receiptInvoice.id} created (balance ₹0). Invoice #${updatedInv.id} updated.`
          );
        }
      } else {
        await invoiceService.createInvoice(tenant.id, authUser.id, {
          paymentMonth: payForm.paymentMonth,
          paymentYear: payForm.paymentYear,
          paymentType: payForm.paymentType,
          paidAmount: payingRaw,
          balanceAmount: balanceRaw === '' ? '0' : balanceRaw,
          discount: discountRaw === '' ? '0' : discountRaw,
          dueDate: balanceNum > 0 ? payForm.dueDate : undefined,
          paymentStatus,
          paymentTimestamp: new Date().toISOString(),
        });
      }
      toast.success(
        settleDueMode
          ? balanceNum > 0
            ? 'Invoice updated. Remaining balance stays in dues.'
            : 'Invoice updated. Balance cleared.'
          : balanceNum > 0
            ? 'Partial payment recorded. Remaining balance stays in dues.'
            : 'Payment recorded. Month is fully paid.'
      );
      await onSuccess?.();
      onClose();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(e?.response?.data?.message || 'Failed to create invoice');
    } finally {
      setPaySubmitting(false);
    }
  };

  return (
    <div className="tenants-notice-overlay" role="presentation" onClick={closeModal}>
      <div
        className="tenants-notice-card tenants-pay-card"
        role="dialog"
        aria-labelledby="pay-invoice-title"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tenants-notice-header">
          <h3 id="pay-invoice-title">{settleDueMode ? 'Update invoice (pay due)' : 'Create Invoice'}</h3>
          <button type="button" className="tenants-notice-close" aria-label="Close" disabled={paySubmitting} onClick={closeModal}>
            ×
          </button>
        </div>
        <p className="tenants-pay-tenant">{tenant.username || tenant.phoneNumber || 'Tenant'}</p>

        {settleDueMode && settleDueSummary ? (
          <div className="tenants-pay-banner tenants-pay-banner--settle" role="status">
            <p className="tenants-pay-banner-title">
              Update invoice
              {sourceInvoiceId != null ? ` #${sourceInvoiceId}` : ''} — {settleDueSummary.month}{' '}
              {settleDueSummary.year}
            </p>
            <p className="tenants-pay-banner-amount">
              Balance due: ₹{Math.round(settleDueSummary.amount).toLocaleString('en-IN')}
            </p>
            <p className="tenants-pay-arrears-hint">
              Updates invoice #{sourceInvoiceId ?? '—'} (balance = due − paying). A <strong>payment receipt</strong>{' '}
              invoice is also created with <strong>balance ₹0</strong>.
            </p>
          </div>
        ) : null}

        {!settleDueMode && payArrearsLoading ? (
          <p className="tenants-pay-arrears-msg">Checking payment status…</p>
        ) : !settleDueMode ? (
          <>
            {payCurrentMonth?.isFullyPaid ? (
              <div className="tenants-pay-banner tenants-pay-banner--paid" role="status">
                <p>
                  This user has already paid rent for <strong>{payCurrentMonth.periodLabel}</strong>.
                </p>
              </div>
            ) : null}
          </>
        ) : null}

        {!settleDueMode && !payArrearsLoading && payArrears.length > 0 ? (
          <div className="tenants-pay-arrears">
            <p className="tenants-pay-arrears-title">Unpaid rent (previous months)</p>
            <div className="tenants-pay-arrears-table-wrap">
              <table className="tenants-pay-arrears-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Rent</th>
                    <th>Status</th>
                    <th className="tenants-pay-arrears-th-action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {payArrears.map((row) => {
                    const rowKey = `${row.month}|${row.year}`;
                    const isSelected = payArrearsSelectedKey === rowKey;
                    return (
                      <tr key={rowKey} className={isSelected ? 'is-selected' : undefined}>
                        <td>{row.periodLabel}</td>
                        <td>₹{row.rent.toLocaleString('en-IN')}</td>
                        <td>
                          <span className="tenants-pay-arrears-status">{row.status}</span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="tenants-pay-arrears-pay-btn"
                            disabled={paySubmitting}
                            onClick={() => applyArrearsRowToPayForm(row)}
                          >
                            Pay
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="tenants-pay-arrears-hint">Choose Pay for a month, then record payment below.</p>
          </div>
        ) : null}

        <form className="tenants-pay-form" onSubmit={handleCreatePayInvoice}>
          {!settleDueMode ? (
          <div className="tenants-pay-grid">
            <label className="tenants-pay-field">
              <span>Payment Month</span>
              <select
                value={payForm.paymentMonth}
                onChange={(e) => setPayForm((f) => ({ ...f, paymentMonth: e.target.value }))}
                required
                disabled={paySubmitting}
              >
                {PAYMENT_MONTHS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
            <label className="tenants-pay-field">
              <span>Payment Year</span>
              <select
                value={payForm.paymentYear}
                onChange={(e) => setPayForm((f) => ({ ...f, paymentYear: e.target.value }))}
                required
                disabled={paySubmitting}
              >
                {payYearOptions.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
            <label className="tenants-pay-field">
              <span>Payment Type</span>
              <select
                value={payForm.paymentType}
                onChange={(e) =>
                  setPayForm((f) => ({
                    ...f,
                    paymentType: e.target.value as Invoice['paymentType'],
                  }))
                }
                required
                disabled={paySubmitting}
              >
                <option value="RENT">Rent</option>
                <option value="ADVANCE">Advance</option>
                <option value="REFUND">Refund</option>
              </select>
            </label>
          </div>
          ) : null}
          {settleDueMode ? (
            <div className="tenants-pay-grid tenants-pay-grid--amounts tenants-pay-grid--settle-due">
              <label className="tenants-pay-field">
                <span>Due amount</span>
                <input
                  type="text"
                  readOnly
                  value={`₹${dueTotal.toLocaleString('en-IN')}`}
                  className="tenants-pay-field--readonly"
                  aria-readonly
                />
              </label>
              <label className="tenants-pay-field">
                <span>Paying amount</span>
                <input
                  type="number"
                  min={1}
                  max={dueTotal}
                  step="1"
                  value={payForm.paidAmount}
                  onChange={(e) => handlePayingAmountChange(e.target.value)}
                  required
                  disabled={paySubmitting}
                />
              </label>
              <label className="tenants-pay-field">
                <span>Balance amount</span>
                <input
                  type="text"
                  readOnly
                  value={`₹${balanceAfterSettle.toLocaleString('en-IN')}`}
                  className="tenants-pay-field--readonly"
                  aria-readonly
                />
              </label>
            </div>
          ) : (
          <div className="tenants-pay-grid tenants-pay-grid--amounts">
            <label className="tenants-pay-field">
              <span>Paid Amount</span>
              <input
                type="number"
                min={0}
                step="1"
                value={payForm.paidAmount}
                onChange={(e) => handlePayingAmountChange(e.target.value)}
                required
                disabled={paySubmitting}
              />
            </label>
            <label className="tenants-pay-field">
              <span>Balance Amount (only if still due)</span>
              <input
                type="number"
                min={0}
                step="1"
                placeholder="0"
                value={payForm.balanceAmount}
                onChange={(e) => handlePayBalanceChange(e.target.value)}
                disabled={paySubmitting}
              />
            </label>
            <label className="tenants-pay-field tenants-pay-field--discount">
              <span>Discount</span>
              <input
                type="number"
                min={0}
                step="1"
                placeholder="Enter Discount"
                value={payForm.discount}
                onChange={(e) => setPayForm((f) => ({ ...f, discount: e.target.value }))}
                disabled={paySubmitting}
              />
              <span className="tenants-pay-advance-hint">Advance: {formatAdvance(tenant)}</span>
            </label>
          </div>
          )}
          {!settleDueMode && payForm.balanceAmount.trim() !== '' && Number(payForm.balanceAmount) > 0 ? (
            <label className="tenants-pay-field tenants-pay-field--due">
              <span>Due Date</span>
              <input
                type="date"
                value={payForm.dueDate}
                onChange={(e) => setPayForm((f) => ({ ...f, dueDate: e.target.value }))}
                required
                disabled={paySubmitting}
              />
            </label>
          ) : null}
          <div className="tenants-pay-footer">
            <button type="button" className="tenants-pay-btn tenants-pay-btn--close" disabled={paySubmitting} onClick={closeModal}>
              Close
            </button>
            <button type="submit" className="tenants-pay-btn tenants-pay-btn--create" disabled={paySubmitting}>
              {paySubmitting ? 'Saving…' : settleDueMode ? 'Update invoice' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PayInvoiceModal;
